import React, { useState, useEffect, useCallback } from 'react';
import { UserAuthActionType } from '@bb/users';
import moment from 'moment';
import { Pagination, TableProps } from 'components';
import { UserController } from 'api';
import { BorderedTable, Spacer } from './StyledComponents';
import TableEntry from './TableEntry';

const columns: TableProps['columns'] = [
  {
    title: 'DATE',
    dataIndex: 'date',
    key: 'date',
    width: 110,
    render: value => <TableEntry value={value} />,
  },
  {
    title: 'TIME',
    dataIndex: 'time',
    key: 'time',
    width: 100,
    render: value => <TableEntry value={value} />,
  },
  {
    title: 'ACTION',
    dataIndex: 'action',
    key: 'action',
    width: 45,
    render: value => <TableEntry value={value} />,
  },
  {
    title: 'DEVICE/BROWSER',
    dataIndex: 'userAgent',
    key: 'userAgent',
    width: 230,
    render: value => <TableEntry value={value} />,
  },
  {
    title: 'IP ADDRESS',
    dataIndex: 'ip',
    key: 'ip',
    render: value => <TableEntry value={value} />,
  },
];

interface DetailsTableProps {
  userId?: string;
}

const AuthActionStringMap: Record<UserAuthActionType, string> = {
  logIn: 'Login',
  logOut: 'Logout',
};

const ActivityTable = ({ userId }: DetailsTableProps) => {
  const [userActions, setUserActions] = useState<TableProps['dataSource']>([]);

  const [page, setPage] = useState(1);
  const [userActionCount, setUserActionCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(
    async (pageNumber = 1) => {
      const data = await UserController.getUserAuthActions({
        page: pageNumber,
        userId: userId!,
        itemsPerPage: 3,
      });
      const actions: TableProps['dataSource'] = data.actions?.map(
        userAuthAction => ({
          key: userAuthAction._id,
          date: moment(userAuthAction.timestamp).format(
            'ddd MMMM [\n] D, YYYY',
          ),
          time: moment(userAuthAction.timestamp).format('H:mm:ss'),
          action:
            // @ts-expect-error: mismatch type
            AuthActionStringMap[userAuthAction.action] || userAuthAction.action,
          userAgent: userAuthAction.userAgent,
          ip: userAuthAction.ipAddress,
        }),
      );

      setUserActions(actions || []);
      setUserActionCount(data.count);
      setLoading(false);
    },
    [userId],
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const handlePageChange = (selectedPage: number) => {
    if (loading) return;
    setPage(selectedPage);
    setLoading(true);
    fetchPage(selectedPage);
  };

  return (
    <>
      <BorderedTable
        dataSource={userActions}
        columns={columns}
        pagination={false}
        loading={loading}
        type="primary"
        hideShadow
        scroll={{ x: true }}
      />
      <Spacer $height={10} />
      <Pagination
        current={page}
        pageSize={3}
        total={userActionCount}
        showSizeChanger={false}
        onChange={handlePageChange}
        disabled={loading}
      />
    </>
  );
};

export default ActivityTable;
